import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./OurProducts.css";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const OurProducts = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const navigate = useNavigate();
  useScrollAnimation();

  // Product data with image links
  const categories = [
    {
      id: "fresh-produce",
      name: "Fresh Produce",
      description:
        "Premium fruits and vegetables from Kenya's fertile highlands",
      image: "../../../assets/images/categories/fresh-products.png",
      products: [
        {
          id: 1,
          name: "Avocados (Hass & Fuerte)",
          description:
            "Premium export-quality avocados with perfect ripeness and flavor.",
          image: "../../../assets/images/products/avocado-prd.png",
        },
        {
          id: 2,
          name: "Mangoes (Kent & Apple)",
          description:
            "Sweet, juicy mangoes harvested at peak season for optimal taste.",
          image: "../../../assets/images/products/mangoes-prd.png",
        },
        {
          id: 3,
          name: "French Beans",
          description:
            "Tender, crisp French beans grown in the highlands of Kenya.",
          image: "../../../assets/images/products/french-beans-prd.png",
        },
        {
          id: 4,
          name: "Snow Peas",
          description:
            "Sweet and crunchy snow peas, carefully selected for export.",
          image: "../../../assets/images/products/snow-peas-prd.JPG",
        },
      ],
    },
    {
      id: "tea",
      name: "Tea",
      description:
        "World-renowned tea leaves from Kenya's high-altitude plantations",
      image: "../../../assets/images/categories/tea-products.png",
      products: [
        {
          id: 5,
          name: "Black CTC Tea",
          description:
            "Rich, full-bodied black tea with robust flavor and aroma.",
          image: "../../../assets/images/products/black-tea-prd.JPG",
        },
        {
          id: 6,
          name: "Purple Tea",
          description:
            "Antioxidant-rich purple tea, a unique Kenyan specialty.",
          image: "../../../assets/images/products/purple-tea-prd.JPEG",
        },
        {
          id: 7,
          name: "Green Tea",
          description:
            "Delicate green tea with fresh, vegetal notes and health benefits.",
          image: "../../../assets/images/products/green-tea-prd.png",
        },
        {
          id: 8,
          name: "Herbal Tea Blends",
          description: "Custom herbal tea blends featuring Kenyan ingredients.",
          image: "../../../assets/images/products/herbal-tea-prd.JPG",
        },
      ],
    },
    {
      id: "coffee",
      name: "Coffee",
      description:
        "Premium Arabica coffee beans from the slopes of Mount Kenya",
      image: "../../../assets/images/categories/coffee-products.png",
      products: [
        {
          id: 9,
          name: "AA Grade Coffee",
          description:
            "The largest bean size with exceptional flavor complexity.",
          image: "../../../assets/images/products/aa-coffee.prd.png",
        },
        {
          id: 10,
          name: "AB Grade Coffee",
          description: "Medium bean size with balanced acidity and body.",
          image: "../../../assets/images/products/ab-coffee-prd.png",
        },
        {
          id: 11,
          name: "PB Grade Coffee",
          description: "Peaberry beans with concentrated flavor and sweetness.",
          image: "../../../assets/images/products/pb-coffee-prd.png",
        },
        {
          id: 12,
          name: "Specialty Blends",
          description: "Custom coffee blends for discerning clients worldwide.",
          image:
            "../../../assets/images/products/coffee-specialty-blend-prd.png",
        },
      ],
    },
    {
      id: "nuts-others",
      name: "Commodities (Nuts & Others)",
      description:
        "High-value Kenyan commodities including premium nuts and specialty products",
      image: "../../../assets/images/categories/commodities-products.jpg",
      products: [
        {
          id: 13,
          name: "Macadamia Nuts",
          description:
            "Rich, buttery macadamia nuts carefully processed for premium quality.",
          image: "../../../assets/images/products/macadamia-prd.jpg",
        },
        {
          id: 14,
          name: "Cashew Nuts",
          description:
            "Creamy cashew nuts from Kenya’s coastal regions, graded to international standards.",
          image: "../../../assets/images/products/cashew-prd.png",
        },
      ],
    },
  ];

  // Filter products based on active category
  const filteredProducts =
    activeCategory === "all"
      ? categories.flatMap((category) => category.products)
      : categories.find((cat) => cat.id === activeCategory)?.products || [];

  // Add animation delays on component mount
  useEffect(() => {
    const animateElements = document.querySelectorAll(".animate-on-load");
    animateElements.forEach((element, index) => {
      element.style.animationDelay = `${0.2 + index * 0.1}s`;
    });
  }, []);

  return (
    <section className="our-products">
      <div className="container">
        <div className="section-header">
          <h2 className="animate-on-scroll">Our Premium Products</h2>
          <p className="animate-on-scroll delay-1">
            Exporting the finest Kenyan agricultural products to global markets
          </p>
        </div>

        {/* Category Filter */}
        <div className="category-filter animate-on-scroll delay-2">
          <button
            className={`filter-btn ${activeCategory === "all" ? "active" : ""}`}
            onClick={() => setActiveCategory("all")}
          >
            All Products
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`filter-btn ${
                activeCategory === category.id ? "active" : ""
              }`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="products-grid">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product, index) => (
              <div key={product.id} className={`product-card `}>
                <div className="product-image">
                  <img
                    src={product.image}
                    alt={product.name}
                    onError={(e) => {
                      // Fallback if image fails to load
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="image-placeholder">
                    <span>{product.name.split(" ")[0]}</span>
                  </div>
                  <div className="product-overlay">
                    <button className="btn-quick-view">Quick View</button>
                  </div>
                </div>
                <div className="product-content">
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                  <div className="product-actions">
                    <button
                      className="btn-learn-more"
                      onClick={() => navigate(`/product/${product.id}`)}
                    >
                      Learn More
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 12H19M19 12L12 5M19 12L12 19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      className="btn-inquire"
                      onClick={() => navigate(`/contact`)}
                    >
                      Inquire Now
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-products animate-on-scroll">
              <p>No products found in this category.</p>
            </div>
          )}
        </div>
        <div className="section-header">
          <h2 className="animate-left">Our Premium Products</h2>
          <p className="animate-right">
            Exporting the finest Kenyan agricultural products to global markets
          </p>
        </div>
        {/* Category Highlights */}
        <div className="category-highlights">
          {categories.map((category, index) => (
            <div
              key={category.id}
              className={`category-card animate-on-scroll delay-${index}`}
            >
              <div className="category-image">
                <img
                  src={category.image}
                  alt={category.name}
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="image-placeholder large">
                  <span>{category.name}</span>
                </div>
                <div className="category-overlay"></div>
              </div>
              <div className="category-content">
                <h3>{category.name}</h3>
                <p>{category.description}</p>
                <button
                  className="btn-explore-category"
                  onClick={() => navigate(`/products/${category.id}`)}
                >
                  Explore {category.name}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="products-cta animate-on-scroll">
          <div className="cta-content">
            <h3 className="animate-right">
              Ready to source quality Kenyan products?
            </h3>
            <p className="animate-left">
              Contact us for quotes, samples, and partnership opportunities
            </p>
            <div className="cta-buttons animate-on-scroll delay-1">
              <button className="btn btn-primary">
                Request a Quote
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 4H20V16H4V4Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 10H20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 16V20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 16V20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button className="btn btn-secondary">
                Download Catalog
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 10L12 15L17 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 15V3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurProducts;
